"use client";

import React from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { ShieldCheck, TrendingUp, Users } from "lucide-react";
interface StatItemProps {
  value: string;
  label: string;
  index: number;
}
function StatItem({
  value,
  label,
  index
}: StatItemProps) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.5
  });
  return <div ref={ref} data-index={index} className="flex flex-col gap-2">
      <span className="text-5xl lg:text-7xl font-bold font-default tracking-tighter transition-colors duration-1000 ease-in-out" style={{
      WebkitTextStroke: "1px var(--color-primary)",
      color: isVisible ? "var(--color-primary)" : "transparent"
    }}>
        {value}
      </span>
      <span className="text-muted-foreground font-medium text-lg font-sans uppercase tracking-widest">{label}</span>
    </div>;
}
export default function OurAuthority() {
  const {
    ref: sectionRef,
    isVisible: sectionVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: textRef,
    isVisible: textVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const stats = [{
    value: "2018",
    label: "The First Meeting"
  }, {
    value: "1,460",
    label: "Days of Adventure"
  }, {
    value: "Yes",
    label: "The Final Answer"
  }];
  return <section data-section-id="224603" id="the-proposal" className="dark bg-background text-foreground py-[120px] px-4 md:px-8 overflow-hidden relative">
      {}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/5 rounded-lg blur-[120px] -z-10 pointer-events-none" />

      <div ref={sectionRef} className={`max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-[6fr_4fr] gap-16 lg:gap-24 items-center transition-all duration-1000 ${sectionVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
        {}
        <div className="relative w-full aspect-[4/5] lg:aspect-auto lg:h-[800px] rounded-lg overflow-hidden group">
          <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678925/img/jane_liam_proposal_moment.png" alt="Jane and Liam's romantic proposal moment in the sunset" className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105" />
          
          {}
          <div className="absolute inset-0 bg-gradient-to-t from-background/60 via-transparent to-transparent" />

          {}
          <div className="absolute bottom-8 left-8 right-8 md:right-auto md:left-12 md:w-96 backdrop-blur-md bg-card/90 border border-border/50 p-8 rounded-lg shadow-2xl transition-transform duration-700 translate-y-0 hover:-translate-y-2">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 rounded-lg bg-accent/30 flex items-center justify-center border border-accent/50">
                <ShieldCheck className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-xl font-semibold font-default text-primary">A Promise Made</h3>
            </div>
            <p className="text-primary/80 leading-relaxed font-default italic">
              Under the golden canopy of the Amalfi coast, Liam asked the question that would change their lives forever.
            </p>
          </div>
        </div>

        {}
        <div ref={textRef} className="flex flex-col justify-center">
          <div className={`transition-all duration-1000 delay-300 ${textVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"}`}>
            <h2 className="text-4xl lg:text-5xl font-bold font-default mb-8 tracking-tight text-foreground">
              The Story of <span className="text-primary italic">The Proposal</span>
            </h2>
            
            <div className="space-y-6 text-lg text-muted-foreground mb-16 font-default">
              <p>
                It wasn't just a question; it was the culmination of four years of shared laughter, quiet mornings, and the kind of love that feels like coming home. Liam had planned the moment for months, choosing a secluded cliffside that overlooked the shimmering Mediterranean.
              </p>
              <p>
                As the sun dipped below the horizon, painting the sky in hues of blush and gold, he spoke of their future. Jane’s "yes" was whispered through tears of joy, marking the beginning of their most beautiful chapter yet.
              </p>
            </div>

            {}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-12">
              {stats.map((stat, i) => <StatItem key={i} index={i} value={stat.value} label={stat.label} />)}
            </div>
          </div>
        </div>
      </div>
    </section>;
}